import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder,
  LinkedIn as LinkedInIcon
} from "@mui/icons-material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export const resumeMeta = {
  hasPhoto: false,
  columns: 2,
  colorOptions: [
    { value: '#2196f3', label: 'Blue' },
    { value: '#4caf50', label: 'Green' },
    { value: '#ff9800', label: 'Orange' },
    { value: '#9c27b0', label: 'Purple' },
    { value: '#f44336', label: 'Red' }
  ]
};

const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

const defaultData = {
  firstName: "Richard",
  lastName: "Sanchez",
  currentPosition: "Marketing Manager",
  phone: "+123-456-7890",
  email: "hello@reallygreatsite.com",
  city: "Any City, State",
  website: "",
  profile: "Dynamic and result-oriented Marketing Manager with 8+ years of experience. Proven expertise in market analysis, digital growth, and campaign management. Committed to driving brand innovation and achieving organizational targets.",
};

const defaultSectionOrder = [
  'profile',
  'workExperience',
  'education',
  'certifications',
  'accomplishments',
  'volunteering',
  'languages',
  'skills',
  'interests',
  'additionalInfo',
  'references'
];

const Resume32 = ({
  color = "#27ae60",
  font = "Montserrat, sans-serif",
  fontSize = "14px",
  headingSize = "20px",
  fontStyle = "normal",
  sectionSpacing = 10,
  paragraphSpacing = 30,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const data = {
    ...defaultData,
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };

  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const mainContentRef = useRef(null);

  const [sectionOrder, setSectionOrder] = useState(defaultSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  // ─── SHARED STYLE HELPERS ───────────────────────────────────────────────────

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    // KEY FIX: prevent text from escaping its column
    wordBreak: "break-word",
    overflowWrap: "break-word",
    hyphens: "auto",
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles,
  });

  const getSectionHeaderStyle = () => ({
    fontFamily: font,
    fontSize: headingSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
    fontWeight: "bold",
    marginBottom: `${sectionSpacing}px`,
    marginTop: `${sectionSpacing / 2}px`,
    color: color,
    borderBottom: `${lineWeight}px solid ${color}`,
    paddingBottom: "4px",
    overflow: "hidden",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  });

  const getContentSpacingStyle = () => ({
    mb: `${sectionSpacing}px`,
    ...globalFontStyle,
    width: "100%",
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  });

  const getParagraphSpacingStyle = () => ({
    mb: `${paragraphSpacing}px`,
    ...globalFontStyle,
    overflow: "hidden",
  });

  const getListItemSpacingStyle = () => ({
    mb: `${paragraphSpacing / 2}px`,
    ...globalFontStyle,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    maxWidth: "100%",
    overflow: "hidden",
  });

  // ─── DATA LOADING ─────────────────────────────────────────────────────────

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));

    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));

    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);

    setSavedAdditionalInfo(JSON.parse(localStorage.getItem("additionalInfo")) || []);
    setSavedLanguages(JSON.parse(localStorage.getItem("languagesList")) || []);
    setSavedAccomplishments(JSON.parse(localStorage.getItem("accomplishmentsList")) || []);
    setSavedCertifications(JSON.parse(localStorage.getItem("certificationsList")) || []);
    setSavedReferences(JSON.parse(localStorage.getItem("referencesList")) || []);
    setSoftwareSkills(JSON.parse(localStorage.getItem("softwareSkills")) || []);
    setSavedVolunteering(JSON.parse(localStorage.getItem("volunteering")) || []);
    setSavedInterests(JSON.parse(localStorage.getItem("interests")) || []);
    setSavedWebsites(JSON.parse(localStorage.getItem("websites")) || []);

    const userId = localStorage.getItem("userId");
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      currentCustomSections.forEach(sectionName => {
        const userCustomKey = `custom_${userId}_${sectionName}`;
        try {
          const customSectionData = localStorage.getItem(userCustomKey);
          if (customSectionData) {
            const parsedData = JSON.parse(customSectionData);
            const validItems = (parsedData.items || []).filter(item => item && item.trim() !== '');
            if (validItems.length > 0) sectionsData[sectionName] = validItems;
          }
        } catch (error) {
          console.error(`Error loading custom section ${sectionName}:`, error);
        }
      });
    }

    setCustomSections(sectionsData);

    const savedOrder = localStorage.getItem("resume32SectionOrder");
    if (savedOrder) {
      setSectionOrder(JSON.parse(savedOrder));
    } else {
      const currentCustomIds = Object.keys(sectionsData).map(n => `custom_${n}`);
      setSectionOrder([...defaultSectionOrder, ...currentCustomIds]);
    }
  }, []);

  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem("resume32SectionOrder", JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentCustomIds = Object.keys(customSections).map(n => `custom_${n}`);
      const filtered = sectionOrder.filter(s => !s.startsWith('custom_'));
      const newOrder = [...filtered, ...currentCustomIds];
      if (JSON.stringify(newOrder) !== JSON.stringify(sectionOrder)) {
        setSectionOrder(newOrder);
        localStorage.setItem("resume32SectionOrder", JSON.stringify(newOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const filtered = sectionOrder.filter(s => !s.startsWith('custom_'));
      if (filtered.length !== sectionOrder.length) {
        setSectionOrder(filtered);
        localStorage.setItem("resume32SectionOrder", JSON.stringify(filtered));
      }
    }
  }, [customSections]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // ─── DRAG & DROP ──────────────────────────────────────────────────────────

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
    let newOrder = [...sectionOrder];
    const draggedSection = newOrder[dragData.index];
    newOrder.splice(dragData.index, 1);
    newOrder.splice(targetIndex, 0, draggedSection);
    setSectionOrder(newOrder);
    localStorage.setItem("resume32SectionOrder", JSON.stringify(newOrder));
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetSectionOrder = () => {
    const customIds = Object.keys(customSections).map(n => `custom_${n}`);
    const newOrder = [...defaultSectionOrder, ...customIds];
    setSectionOrder(newOrder);
    localStorage.setItem("resume32SectionOrder", JSON.stringify(newOrder));
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  // ─── SECTION RENDERER ─────────────────────────────────────────────────────
  // KEY FIX: every container gets width:100% + overflow:hidden + boxSizing:border-box
  // every Typography gets wordBreak:break-word + overflowWrap:break-word

  const renderSection = (sectionId) => {
    const className = "resume-section";

    const wrapSx = {
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      mb: `${sectionSpacing}px`,
    };

    switch (sectionId) {

      case 'education':
        return educationEntries.length > 0 ? (
          <div key="education" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>EDUCATION</Typography>
            {educationEntries.map((edu) => {
              const institutionName = edu.educationLevel === "school" ? edu.schoolName : edu.collegeName;
              const degreeName = edu.educationLevel === "school" ? edu.standard : edu.degreeName;
              const fieldOfStudy = edu.educationLevel === "school" ? edu.stream : edu.specialization;
              const location = edu.educationLevel === "school" ? edu.schoolLocation : edu.collegeLocation;
              return (
                <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                  <Typography sx={getTextStyle({ fontWeight: "bold", fontSize: "14px" })}>{institutionName}</Typography>
                  <Typography sx={getTextStyle()}>{degreeName}{fieldOfStudy ? ` - ${fieldOfStudy}` : ""}</Typography>
                  <Typography sx={getTextStyle({ fontSize: "12px", color: "#666" })}>
                    {location}
                    {edu.percentage ? ` | ${edu.percentage}%` : ""}
                    {edu.cgpa ? ` | CGPA: ${edu.cgpa}` : ""}
                    {edu.grade ? ` | Grade: ${edu.grade}` : ""}
                  </Typography>
                  <Typography sx={getTextStyle({ fontStyle: 'italic', color: '#666', fontSize: "12px" })}>
                    {formatDate(edu.gradMonth, edu.gradYear)}
                  </Typography>
                </Box>
              );
            })}
          </div>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <div key="languages" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>LANGUAGES</Typography>
            <Box sx={{ width: '100%', maxWidth: "100%", overflow: "hidden", mb: `${sectionSpacing}px` }}>
              {savedLanguages.map((lang, index) => (
                <Typography key={index} sx={getListItemSpacingStyle()}>
                  • {lang.name}{lang.level ? ` (${lang.level})` : ""}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <div key="skills" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>SKILLS</Typography>
            <Box sx={{ width: '100%', maxWidth: "100%", overflow: "hidden", mb: `${sectionSpacing}px` }}>
              {savedSkills.map((skill, index) => (
                <Typography key={index} sx={getListItemSpacingStyle()}>
                  • {skill.name}{skill.rating ? ` (${skill.rating}%)` : ""}
                </Typography>
              ))}
              {softwareSkills.map((skill, index) => (
                <Typography key={`software-${index}`} sx={getListItemSpacingStyle()}>
                  • {skill.name}{skill.level ? ` (${skill.level}%)` : ""}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'profile':
        return (savedSummary || data.profile) ? (
          <div key="profile" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>ABOUT ME</Typography>
            <Typography sx={getTextStyle({
              color: "#444",
              lineHeight: `${lineSpacing}px`,
              textAlign: "justify",
              mb: `${sectionSpacing}px`,
            })}>
              {savedSummary || data.profile}
            </Typography>
          </div>
        ) : null;

      case 'workExperience':
        return workExperiences && workExperiences.length > 0 ? (
          <div key="workExperience" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>WORK EXPERIENCE</Typography>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={getTextStyle({ fontWeight: "bold", fontSize: "16px", color: "#333" })}>
                  {work.jobTitle}
                </Typography>
                {/* Title + date row with flex-wrap to prevent overflow */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "baseline", overflow: "hidden" }}>
                  <Typography sx={getTextStyle({ fontSize: "13px", color: color, fontWeight: "600", flex: "1 1 auto", minWidth: 0 })}>
                    {work.employer}
                  </Typography>
                  <Typography sx={{ ...globalFontStyle, fontSize: "13px", color: color, fontWeight: "600", flex: "0 0 auto", whiteSpace: "nowrap" }}>
                    {formatDate(work.startMonth, work.startYear)} –{" "}
                    {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                {work.description && (
                  <Box
                    sx={{
                      width: "100%", maxWidth: "100%", overflow: "hidden",
                      "& strong": { fontWeight: "bold", ...globalFontStyle },
                      "& em": { fontStyle: "italic", ...globalFontStyle },
                      "& u": { textDecoration: "underline", ...globalFontStyle },
                      "& *": { ...globalFontStyle, wordBreak: "break-word", overflowWrap: "break-word" },
                      ...globalFontStyle,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: work.description
                        .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word;">$1</strong>`)
                        .replace(/\*(.*?)\*/g, `<em style="font-family: ${font}; font-size: ${fontSize}; line-height: ${lineSpacing}px; word-break: break-word;">$1</em>`)
                        .replace(/<u>(.*?)<\/u>/g, `<u style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word;">$1</u>`)
                        .replace(/\n/g, "<br/>"),
                    }}
                  />
                )}
              </Box>
            ))}
          </div>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <div key="certifications" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>CERTIFICATIONS</Typography>
            {savedCertifications.map((cert, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={getTextStyle({ fontWeight: "bold" })}>{cert.name}</Typography>
                <Typography sx={getTextStyle()}>{cert.provider}{cert.year ? ` (${cert.year})` : ""}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <div key="accomplishments" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>ACCOMPLISHMENTS</Typography>
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>• {acc}</Typography>
            ))}
          </div>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <div key="volunteering" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>VOLUNTEERING</Typography>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px`, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
                <Typography sx={getTextStyle({ fontWeight: "bold" })}>{vol.subtopic}</Typography>
                <Typography sx={getTextStyle({ fontStyle: 'italic', color: '#666' })}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography sx={getTextStyle()}>{vol.content}</Typography>
              </Box>
            ))}
          </div>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <div key="interests" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>INTERESTS</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', width: "100%", maxWidth: "100%", overflow: "hidden" }}>
              {savedInterests.map((interest, index) => (
                <Typography key={index} sx={getTextStyle({ mr: 2, mb: `${paragraphSpacing / 2}px` })}>
                  • {interest}
                </Typography>
              ))}
            </Box>
          </div>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <div key="additionalInfo" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>ADDITIONAL INFORMATION</Typography>
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>• {info}</Typography>
            ))}
          </div>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <div key="references" className={className} style={getContentSpacingStyle()}>
            <Typography component="div" style={getSectionHeaderStyle()}>REFERENCES</Typography>
            {savedReferences.map((ref, index) => (
              <Typography key={index} sx={getListItemSpacingStyle()}>• {ref}</Typography>
            ))}
          </div>
        ) : null;

      case 'websites':
        return null;

      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = (customSections[customSectionName] || []).filter(item => item && item.trim() !== '');
          return items.length > 0 ? (
            <div key={sectionId} className={className} style={getContentSpacingStyle()}>
              <Typography component="div" style={getSectionHeaderStyle()}>
                {customSectionName.replace(/_/g, ' ').toUpperCase()}
              </Typography>
              {items.map((item, i) => (
                <Typography key={i} sx={getListItemSpacingStyle()}>• {item}</Typography>
              ))}
            </div>
          ) : null;
        }
        return null;
    }
  };

  const renderSections = () => sectionOrder.map(id => renderSection(id)).filter(Boolean);

  const getSectionTitle = (sectionId) => {
    const titles = {
      coreSkills: 'Core Skills', education: 'Education', skills: 'Skills',
      languages: 'Languages', profile: 'About Me', workExperience: 'Work Experience',
      certifications: 'Certifications', accomplishments: 'Accomplishments',
      volunteering: 'Volunteering', interests: 'Interests',
      additionalInfo: 'Additional Information', references: 'References'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').replace(/_/g, ' ').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const renderDragPanel = (sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', ...globalFontStyle }}>
        <DragIndicator sx={{ mr: 1 }} />
        Resume Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1, ...globalFontStyle }}>
          ({sections.length} sections)
        </Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? sections.map((sectionId, index) => (
          <Paper
            key={sectionId}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            sx={{
              p: 2, mb: 1, display: 'flex', alignItems: 'center',
              backgroundColor: dragOverIndex === index ? '#f0f0f0' : 'white',
              boxShadow: dragOverIndex === index ? 2 : 1,
              cursor: 'grab', '&:active': { cursor: 'grabbing' }, transition: 'all 0.2s ease',
            }}
          >
            <DragIndicator color="action" />
            <Typography sx={{ ml: 1, flexGrow: 1, ...globalFontStyle }}>{getSectionTitle(sectionId)}</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ ...globalFontStyle }}>{index + 1}</Typography>
          </Paper>
        )) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center', ...globalFontStyle }}>
            No sections available.
          </Typography>
        )}
      </Box>
      <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1, ...globalFontStyle }}>
        Reset Order
      </Button>
    </Box>
  );

  // ─── PAGINATION ──────────────────────────────────────────────────────────

  const paginateContent = useCallback(() => {
    if (!mainContentRef.current) return;
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const headerHeight = 120;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - headerHeight;
    const sections = Array.from(mainContentRef.current.querySelectorAll('.resume-section'));
    let newPages = [];
    let current = [];
    let currentH = 0;
    sections.forEach((section, idx) => {
      const h = section.offsetHeight;
      if (currentH + h > availableHeight) {
        if (current.length > 0) newPages.push([...current]);
        current = [section.outerHTML];
        currentH = h;
      } else {
        current.push(section.outerHTML);
        currentH += h;
      }
      if (idx === sections.length - 1 && current.length > 0) newPages.push([...current]);
    });
    if (newPages.length === 0) newPages.push([]);
    setPages(newPages);
  }, [page.height, topBottomMargin, sectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, customSections, lineWeight]);

  useEffect(() => {
    const id = setTimeout(() => {
      paginateContent();
    }, 500);
    return () => clearTimeout(id);
  }, [
    paginateContent,
    educationEntries, savedSkills, savedSummary, workExperiences,
    savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications,
    savedReferences, softwareSkills, savedVolunteering, savedInterests,
    savedWebsites, customSections, sectionOrder,
    sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, color, lineWeight
  ]);

  // ─── FONT INJECTION FOR dangerouslySetInnerHTML ───────────────────────────

  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    
    const baseStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; max-width: 100%; overflow: hidden; box-sizing: border-box;`;
    const spanStyles = `font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;`;
    
    // For divs - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<div([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; max-width: 100%; overflow: hidden; box-sizing: border-box; ${style}`;
        return `<div${before}style="${newStyle}"`;
      } else {
        return `<div${before}style="${baseStyles}${style}"`;
      }
    });
    htmlContent = htmlContent.replace(/<div(?![^>]*style)([^>]*)>/g, `<div$1style="${baseStyles}">`);
    
    // For p tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<p([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; max-width: 100%; overflow: hidden; box-sizing: border-box; ${style}`;
        return `<p${before}style="${newStyle}"`;
      } else {
        return `<p${before}style="${baseStyles}${style}"`;
      }
    });
    htmlContent = htmlContent.replace(/<p(?![^>]*style)([^>]*)>/g, `<p$1style="${baseStyles}">`);
    
    // For span tags - preserve existing font-size if present
    htmlContent = htmlContent.replace(/<span([^>]*?)style="([^"]*)"/g, (match, before, style) => {
      if (style.includes('font-size:')) {
        const newStyle = `font-family: ${font}; font-style: ${fontStyle}; line-height: ${lineSpacing}px; ${style}`;
        return `<span${before}style="${newStyle}"`;
      } else {
        const newStyle = `${spanStyles}${style}`;
        return `<span${before}style="${newStyle}"`;
      }
    });
    htmlContent = htmlContent.replace(/<span(?![^>]*style)([^>]*)>/g, `<span$1style="${spanStyles}">`);
    
    return htmlContent;
  };

  // ─── HIDDEN MEASUREMENT CONTAINERS ───────────────────────────────────────

  const renderContentForMeasurement = () => (
    <Box ref={mainContentRef} sx={{
      visibility: 'hidden', position: 'absolute', top: -1000,
      width: '100%', overflow: "hidden", boxSizing: "border-box",
      pl: `${sideMargins}px`, pr: `${sideMargins}px`, ...globalFontStyle,
    }}>
      {renderSections()}
    </Box>
  );

  const totalPages = Math.max(pages.length, 1);

  // ─── ROOT RENDER ──────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", position: "relative", m: 5, ...globalFontStyle }}>

      {/* Controls */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' }, ...globalFontStyle }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, ...globalFontStyle }}>Page {currentPage + 1} of {totalPages}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))} disabled={currentPage === totalPages - 1} color="primary">
            <ChevronRight />
          </IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary" sx={{ ...globalFontStyle }}>
                Arrange Sections
              </Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small" sx={{ ...globalFontStyle }}>
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}

      {renderContentForMeasurement()}

      {/* Resume Pages */}
      <Box id="resume-container" sx={{ ...globalFontStyle }}>
        {Array.from({ length: totalPages }).map((_, pageIndex) => (
          <Box
            key={pageIndex}
            className="resume-page"
            sx={{
              width: page.width,
              minHeight: page.height,
              mx: "auto",
              bgcolor: "#fff",
              borderTop: `15px solid ${color}`,
              fontFamily: font,
              boxShadow: pageIndex === currentPage ? 3 : 0,
              display: exporting ? 'flex' : (pageIndex === currentPage ? 'flex' : 'none'),
              flexDirection: "column",
              pageBreakAfter: "always",
              marginTop: `${topBottomMargin}px`,
              marginBottom: `${topBottomMargin}px`,
              // KEY FIX: page-level clip
              overflow: "hidden",
              boxSizing: "border-box",
              ...globalFontStyle,
              "@media print": {
                width: page.width, height: "auto", minHeight: page.height,
                boxShadow: "none", m: 0,
                marginTop: `${topBottomMargin}px`, marginBottom: `${topBottomMargin}px`,
                display: "flex", overflow: "hidden", ...globalFontStyle,
              },
              '@media print': { display: 'flex' }
            }}
          >
            {/* Header – first page only */}
            {pageIndex === 0 && (
              <Box sx={{ p: 5, pb: 2, textAlign: "center", overflow: "hidden" }}>
                <Typography sx={{
                  fontSize: "2.5rem", fontWeight: "800", color: "#333", letterSpacing: 1,
                  wordBreak: "break-word", overflowWrap: "break-word", ...globalFontStyle,
                }}>
                  {data.firstName} {data.lastName}
                </Typography>
                <Typography sx={{
                  color: color, fontWeight: "600", letterSpacing: 3, mb: 2, fontSize: "1.5rem",
                  wordBreak: "break-word", overflowWrap: "break-word", ...globalFontStyle,
                }}>
                  {data.currentPosition}
                </Typography>
                <Grid container justifyContent="center" spacing={3} sx={{ color: "#666", fontSize: "13px", overflow: "hidden" }}>
                  {data.phone && <Grid item sx={{ overflow: "hidden", wordBreak: "break-word" }}>📞 {data.phone}</Grid>}
                  {data.email && <Grid item sx={{ overflow: "hidden", wordBreak: "break-all" }}>✉️ {data.email}</Grid>}
                  {data.city && <Grid item sx={{ overflow: "hidden", wordBreak: "break-word" }}>📍 {data.city}</Grid>}
                  {data.linkedIn && (
                    <Grid item sx={{ overflow: "hidden", wordBreak: "break-all" }}>
                      <LinkedInIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} /> {data.linkedIn}
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {pageIndex === 0 && <Divider sx={{ mx: 5, my: 2 }} />}

            {/* Two-column content */}
            <Box sx={{
              display: "flex", flex: 1,
              p: `${topBottomMargin}px ${sideMargins}px`,
              pt: pageIndex > 0 ? `${sectionSpacing}px` : `${topBottomMargin}px`,
              overflow: "hidden", boxSizing: "border-box",
              ...globalFontStyle,
            }}>
              <Box sx={{ width: "100%", overflow: "hidden", maxWidth: "100%", boxSizing: "border-box" }}>
                {pages[pageIndex] ? (
                  <div
                    style={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box", ...globalFontStyle }}
                    dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pages[pageIndex].join('')) }}
                  />
                ) : (
                  <Typography sx={getTextStyle()}>No content for this page</Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" sx={{ ...globalFontStyle }}>
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3, ...globalFontStyle }}>
            Drag and drop sections to reorder them in your resume.
          </Typography>
          <Box sx={{ width: "100%" }}>
            {renderDragPanel(sectionOrder)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary" sx={{ ...globalFontStyle }}>Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained" sx={{ ...globalFontStyle }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Resume32;